// "Remember this" (RECALL-PEOPLE-SURFACES; design §5.1): a visible icon on a message, and on a
// doc for the text selected in it. A small modal holds the words (editable), "Remember" keeps them
// in the person's own memory. When the memory service cannot take it yet the modal says "Waiting on
// memory service" and keeps the words; nothing errors. Only with the `memory` add-on.
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BrainIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { useFeature } from "../use-feature";
import { memoryMeKey } from "./profile-memory";

export function RememberThis({
  api,
  co,
  text,
  from,
  size = "icon-xs",
}: {
  api: Api;
  co: string;
  /** The words offered: a message's text, or a function reading the doc's selection when clicked. */
  text: string | (() => string);
  from: "message" | "doc" | "chat";
  size?: "icon-xs" | "icon-sm";
}) {
  const memory = useFeature("memory", co);
  const [words, setWords] = useState<string | null>(null);
  if (!memory.enabled || memory.readOnly) return null;
  return (
    <>
      <Button
        variant="ghost"
        size={size}
        aria-label="Remember this"
        title="Remember this"
        data-slot="remember-this"
        // keep the doc's selection: a click on the button must not clear it
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setWords((typeof text === "function" ? text() : text).trim().slice(0, 2000))}
      >
        <BrainIcon aria-hidden="true" />
      </Button>
      {words !== null ? (
        <RememberDialog
          api={api}
          co={co}
          from={from}
          initial={words}
          onClose={() => setWords(null)}
        />
      ) : null}
    </>
  );
}

function RememberDialog({
  api,
  co,
  from,
  initial,
  onClose,
}: {
  api: Api;
  co: string;
  from: "message" | "doc" | "chat";
  initial: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(initial);
  const [waiting, setWaiting] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.client.memoryRemember.post({ params: { co }, body: { text: text.trim(), from } }),
    onSuccess: (r) => {
      if (r.waiting) {
        setWaiting(r.waiting);
        return;
      }
      toast.add({ title: "Remembered", type: "success" });
      void qc.invalidateQueries({ queryKey: memoryMeKey(co) });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="remember-dialog">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              Remember this
              <InfoTip label="About Remember this">
                Kept in your own memory. Only you see it, until you share it.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="remember-text">What to remember</FieldLabel>
            <Textarea
              id="remember-text"
              rows={4}
              maxLength={2000}
              placeholder="Select some text first, or type it here"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setWaiting(null);
              }}
            />
          </Field>
          {waiting ? (
            <p role="status" className="text-[13px] text-muted-foreground">
              {waiting}. Your words stay here until it is ready.
            </p>
          ) : null}
          {save.isError ? (
            <p role="alert" className="text-[13px] text-destructive">
              {problemText(save.error, "That did not go through.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !text.trim()}>
              Remember
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
